import React, { useState } from 'react'
import {
	Box,
	Typography,
	TextField,
	Switch,
	Button,
	IconButton,
	FormControl,
	Select,
	MenuItem,
	Card,
	Fade,
	Avatar,
	Stepper,
	Step,
	StepLabel,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { useNavigate } from 'react-router-dom'
import UserLayout from '../../layouts/UserLayout'
import { api } from '../../utils/api'
import { apiBase } from '../../services/apiBase'
import DeleteIcon from '@mui/icons-material/Delete'
import { compressImage } from '../../utils/helper'
import SaveIcon from '@mui/icons-material/Save'
import CategoryIcon from '@mui/icons-material/Category'
import DescriptionIcon from '@mui/icons-material/Description'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

function CreateProduct() {
	const navigate = useNavigate()
	
	// Modern step-based form
	const [activeStep, setActiveStep] = useState(0)
	const [completedSteps, setCompletedSteps] = useState(new Set())
	
	const [productData, setProductData] = useState({
		name: '',
		nameEn: '',
		nameFr: '',
		nameKo: '',
		nameCn: '',
		price: 0,
		sale_price: 0,
		description: '',
		descriptionEn: '',
		descriptionFr: '',
		descriptionKo: '',
		descriptionCn: '',
		weight: 0,
		status: true,
		category_id: 1,
		image: null,
	})

	// Tách riêng state cho preview
	const [imagePreview, setImagePreview] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const steps = [
		{ label: '01 General', icon: <DescriptionIcon /> },
		{ label: '02 Pricing', icon: <AttachMoneyIcon /> },
		{ label: '03 Settings', icon: <CategoryIcon /> }
	]

	const handleNext = () => {
		setActiveStep((prevStep) => prevStep + 1)
		setCompletedSteps(prev => new Set([...prev, activeStep]))
	}

	const handleStepClick = (step) => {
		setActiveStep(step)
	}

	const isStepCompleted = (step) => {
		return completedSteps.has(step)
	}

	const canProceedToNext = () => {
		switch (activeStep) {
			case 0:
				return productData.name && productData.description
			case 1:
				return productData.price > 0
			case 2:
				return true // Settings step is optional
			default:
				return true
		}
	}

	const handleBackToProductList = () => {
		navigate('/admin/zalo-mini-app/product')
	}

	const handleInputChange = (e) => {
		const { name, value } = e.target

		// Danh sách các trường là number
		const numberFields = ['price', 'sale_price', 'weight']

		setProductData((prev) => ({
			...prev,
			[name]: numberFields.includes(name) ? Number(value) || 0 : value,
		}))
	}

	const handleSwitchChange = (event) => {
		setProductData((prev) => ({
			...prev,
			status: event.target.checked,
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setIsSubmitting(true)
		
		try {
			// Create FormData
			const formData = new FormData()

			// Compress image before uploading
			if (productData.image) {
				const compressedImage = await compressImage(productData.image)
				formData.append('uploadImage', compressedImage)
			}

			// Append other data
			formData.append('name', productData.name)
			formData.append('nameEn', productData.nameEn)
			formData.append('nameFr', productData.nameFr)
			formData.append('nameKo', productData.nameKo)
			formData.append('nameCn', productData.nameCn)
			formData.append('price', productData.price)
			formData.append('sale_price', productData.sale_price)
			formData.append('description', productData.description)
			formData.append('descriptionEn', productData.descriptionEn)
			formData.append('descriptionFr', productData.descriptionFr)
			formData.append('descriptionKo', productData.descriptionKo)
			formData.append('descriptionCn', productData.descriptionCn)
			formData.append('weight', productData.weight)
			formData.append('status', productData.status)
			formData.append('category_id', productData.category_id)

			const response = await apiBase.post(api.ZALO_MINI_APP_CREATE_PRODUCT, formData)

			if (response.status) {
				// Show success animation
				setTimeout(() => {
					alert('Tạo sản phẩm thành công!')
					navigate('/admin/zalo-mini-app/product')
				}, 500)
			}
		} catch (error) {
			console.error('Error creating product:', error)
			let errorMessage = 'Có lỗi xảy ra khi tạo sản phẩm.'

			if (error.message.includes('PayloadTooLargeError') || error.message.includes('request entity too large')) {
				errorMessage = 'Kích thước ảnh quá lớn. Vui lòng thử lại với ảnh nhỏ hơn!'
			} else {
				errorMessage += ` ${error?.message}`
			}

			alert(`${errorMessage}\nVui lòng thử lại!`)
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleImageChange = async (event) => {
		const file = event.target.files[0]
		if (file) {
			// Compress image before setting to state
			const compressedFile = await compressImage(file)
			setProductData((prev) => ({
				...prev,
				image: compressedFile,
			}))
			setImagePreview(URL.createObjectURL(compressedFile))
		}
	}

	const handleDeleteImage = (e) => {
		e.preventDefault()
		e.stopPropagation()
		// Reset cả hai state
		setProductData((prev) => ({
			...prev,
			image: null,
		}))
		setImagePreview(null)
	}

	return (
		<UserLayout>
			<Box sx={{ 
				minHeight: '100vh',
				background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
				position: 'relative',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23cbd5e1" fill-opacity="0.3"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
					opacity: 0.5,
				}
			}}>
				{/* Modal Container */}
				<Box sx={{ 
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					p: 3,
				}}>
					<Card 
						elevation={0}
						sx={{
							width: '100%',
							maxWidth: 800,
							borderRadius: 4,
							overflow: 'hidden',
							background: 'white',
							boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
							border: '1px solid rgba(0,0,0,0.05)',
						}}
					>
						{/* Modal Header */}
						<Box sx={{ 
							p: 4,
							borderBottom: '1px solid #e2e8f0',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Avatar sx={{ 
									bgcolor: '#3b82f6',
									width: 40,
									height: 40,
								}}>
									<ShoppingCartIcon />
								</Avatar>
								<Box>
									<Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
										Add Product
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Add a new product to your store.
									</Typography>
								</Box>
							</Box>
							<IconButton 
								onClick={handleBackToProductList}
								sx={{
									color: '#64748b',
									'&:hover': { 
										bgcolor: '#f1f5f9',
									},
								}}
							>
								<DeleteIcon />
							</IconButton>
						</Box>

						{/* Stepper */}
						<Box sx={{ p: 4, pb: 2 }}>
							<Stepper 
								activeStep={activeStep} 
								alternativeLabel
								sx={{
									'& .MuiStepLabel-root': {
										cursor: 'pointer',
									},
									'& .MuiStepLabel-label': {
										fontWeight: 600,
										fontSize: '0.875rem',
									},
									'& .MuiStepLabel-label.Mui-active': {
										color: '#3b82f6',
									},
									'& .MuiStepLabel-label.Mui-completed': {
										color: '#10b981',
									},
									'& .MuiStepIcon-root': {
										fontSize: '1.5rem',
										color: '#cbd5e1',
									},
									'& .MuiStepIcon-root.Mui-active': {
										color: '#3b82f6',
									},
									'& .MuiStepIcon-root.Mui-completed': {
										color: '#10b981',
									},
								}}
							>
								<Step key="General" completed={isStepCompleted(0)}>
									<StepLabel onClick={() => handleStepClick(0)}>
										01 General
									</StepLabel>
								</Step>
								<Step key="Pricing" completed={isStepCompleted(1)}>
									<StepLabel onClick={() => handleStepClick(1)}>
										02 Pricing
									</StepLabel>
								</Step>
								<Step key="Settings" completed={isStepCompleted(2)}>
									<StepLabel onClick={() => handleStepClick(2)}>
										03 Settings
									</StepLabel>
								</Step>
							</Stepper>
						</Box>

						{/* Form Content */}
						<Box sx={{ p: 4, pt: 0 }}>
							{/* Step 1: General Information */}
							{activeStep === 0 && (
								<Fade in timeout={300}>
									<Box>
										{/* Name Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Việt) *
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='name'
												value={productData.name}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Vietnamese.
											</Typography>
										</Box>

										{/* Name English Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Anh)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameEn'
												value={productData.nameEn}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in English.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Pháp)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameFr'
												value={productData.nameFr}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Tiếng Pháp.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng Hàn)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameKo'
												value={productData.nameKo}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Tiếng Hàn.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Tên Sản Phẩm (Tiếng trung)
											</Typography>
											<TextField
												fullWidth
												placeholder="Universal Design System"
												name='nameCn'
												value={productData.nameCn}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear name in Tiếng TQ.
											</Typography>
										</Box>

										{/* Description Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Việt)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='description'
												multiline
												rows={3}
												value={productData.description}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Vietnamese.
											</Typography>
										</Box>

										{/* Description English Field */}
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Anh)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionEn'
												multiline
												rows={3}
												value={productData.descriptionEn}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in English.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Pháp)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionFr'
												multiline
												rows={3}
												value={productData.descriptionFr}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Tiếng Pháp.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng Hàn)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionKo'
												multiline
												rows={3}
												value={productData.descriptionKo}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in Korea.
											</Typography>
										</Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Mô Tả Sản Phẩm (Tiếng TQ)
											</Typography>
											<TextField
												fullWidth
												placeholder="Powerful Figma Design System for creating landing pages, websites and dashboards."
												name='descriptionCn'
												multiline
												rows={3}
												value={productData.descriptionCn}
												onChange={handleInputChange}
												variant='outlined'
												size='medium'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Give your product a short and clear description in China.
											</Typography>
										</Box>

										{/* Image Upload */}
										<Box>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Hình ảnh
											</Typography>
											<label htmlFor='image-upload'>
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														justifyContent: 'center',
														width: '100%',
														height: 200,
														border: '2px dashed #cbd5e1',
														borderRadius: 2,
														cursor: 'pointer',
														position: 'relative',
														overflow: 'hidden',
														bgcolor: '#f8fafc',
														transition: 'all 0.3s ease',
														'&:hover': {
															borderColor: '#3b82f6',
															bgcolor: '#f1f5f9',
														},
													}}
												>
													{imagePreview ? (
														<Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
															<img
																src={imagePreview}
																alt='Preview'
																style={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
															<IconButton
																onClick={handleDeleteImage}
																size='small'
																sx={{
																	position: 'absolute',
																	top: 8,
																	right: 8,
																	bgcolor: 'rgba(239,68,68,0.9)',
																	color: 'white',
																	'&:hover': {
																		bgcolor: 'rgba(239,68,68,1)',
																	},
																}}>
																<DeleteIcon />
															</IconButton>
														</Box>
													) : (
														<>
															<CloudUploadIcon sx={{ fontSize: 48, color: '#64748b', mb: 2 }} />
															<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
																Drop your images here, or
															</Typography>
															<Button
																variant="outlined"
																size="small"
																sx={{
																	borderColor: '#cbd5e1',
																	color: '#64748b',
																	'&:hover': {
																		borderColor: '#3b82f6',
																		color: '#3b82f6',
																	},
																}}
															>
																Click to browse
															</Button>
														</>
													)}
												</Box>
											</label>
											<input
												id='image-upload'
												type='file'
												accept='image/*'
												onChange={handleImageChange}
												style={{ display: 'none' }}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Add up to 10 images to your product. Used to represent your product during checkout, in email, social sharing and more.
											</Typography>
										</Box>
									</Box>
								</Fade>
							)}

							{/* Step 2: Pricing */}
							{activeStep === 1 && (
								<Fade in timeout={300}>
									<Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Price *
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='price'
												value={productData.price ? productData.price.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setProductData(prev => ({
														...prev,
														price: numValue
													}))
												}}
												type='text'
												size='medium'
												InputProps={{
													startAdornment: (
														<Box sx={{ mr: 1, color: '#64748b' }}>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																₫
															</Typography>
														</Box>
													),
												}}
												inputProps={{
													inputMode: 'numeric',
													pattern: '[0-9]*',
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set the price for your product in Vietnamese Dong (VND).
											</Typography>
										</Box>

										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Sale Price
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='sale_price'
												value={productData.sale_price ? productData.sale_price.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setProductData(prev => ({
														...prev,
														sale_price: numValue
													}))
												}}
												type='text'
												size='medium'
												InputProps={{
													startAdornment: (
														<Box sx={{ mr: 1, color: '#ef4444' }}>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																₫
															</Typography>
														</Box>
													),
												}}
												inputProps={{
													inputMode: 'numeric',
													pattern: '[0-9]*',
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set a sale price to offer discounts (optional).
											</Typography>
										</Box>
									</Box>
								</Fade>
							)}

							{/* Step 3: Settings */}
							{activeStep === 2 && (
								<Fade in timeout={300}>
									<Box>
										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Category
											</Typography>
											<FormControl fullWidth size='medium'>
												<Select
													name='category_id'
													value={productData.category_id}
													onChange={handleInputChange}
													sx={{
														borderRadius: 2,
														'& .MuiOutlinedInput-notchedOutline': {
															borderColor: '#cbd5e1',
															'&:hover': {
																borderColor: '#3b82f6',
															},
														},
														'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													}}
												>
													<MenuItem value={1}>Nem nướng</MenuItem>
													<MenuItem value={2}>Nem sống</MenuItem>
													<MenuItem value={3}>Món ăn kèm</MenuItem>
													<MenuItem value={4}>Chả giò</MenuItem>
												</Select>
											</FormControl>
										</Box>

										<Box sx={{ mb: 4 }}>
											<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
												Weight (kg)
											</Typography>
											<TextField
												fullWidth
												placeholder="0"
												name='weight'
												type='text'
												value={productData.weight ? productData.weight.toLocaleString('vi-VN') : ''}
												onChange={(e) => {
													const value = e.target.value.replace(/[^\d]/g, '')
													const numValue = parseInt(value) || 0
													setProductData(prev => ({
														...prev,
														weight: numValue
													}))
												}}
												size='medium'
												InputProps={{
													endAdornment: (
														<Typography variant='body2' color='text.secondary' sx={{ mr: 2 }}>
															kg
														</Typography>
													),
												}}
												variant='outlined'
												sx={{
													'& .MuiOutlinedInput-root': {
														borderRadius: 2,
														'&:hover fieldset': {
															borderColor: '#3b82f6',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#3b82f6',
															borderWidth: 2,
														},
													},
												}}
											/>
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
												Set the weight of your product in kilograms (kg).
											</Typography>
										</Box>

										<Box sx={{ 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'space-between',
											p: 2,
											bgcolor: '#f8fafc',
											borderRadius: 2,
											border: '1px solid #e2e8f0',
										}}>
											<Box>
												<Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
													Product Status
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{productData.status ? 'Active' : 'Inactive'}
												</Typography>
											</Box>
											<Switch 
												checked={productData.status} 
												onChange={handleSwitchChange} 
												color='primary'
												size='medium'
											/>
										</Box>
									</Box>
								</Fade>
							)}
						</Box>

						{/* Footer Actions */}
						<Box sx={{ 
							p: 4,
							borderTop: '1px solid #e2e8f0',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end', // Đẩy nút sang phải
						}}>
							<Button
								variant="outlined"
								sx={{
									borderColor: '#cbd5e1',
									color: '#64748b',
									display: 'none', // Ẩn nút nhưng giữ layout
									'&:hover': {
										borderColor: '#3b82f6',
										color: '#3b82f6',
									},
								}}
							>
								Save as draft - Đang phát triển
							</Button>

							{activeStep < steps.length - 1 ? (
								<Button
									variant="contained"
									onClick={handleNext}
									disabled={!canProceedToNext()}
									endIcon={<ArrowBackIosNewIcon sx={{ transform: 'rotate(180deg)' }} />}
									sx={{
										bgcolor: '#3b82f6',
										'&:hover': {
											bgcolor: '#2563eb',
										},
										'&:disabled': {
											bgcolor: '#cbd5e1',
										},
									}}
								>
									Next step
								</Button>
							) : (
								<Button
									variant="contained"
									onClick={handleSubmit}
									disabled={isSubmitting || !canProceedToNext()}
									startIcon={isSubmitting ? null : <SaveIcon />}
									sx={{
										bgcolor: '#10b981',
										'&:hover': {
											bgcolor: '#059669',
										},
										'&:disabled': {
											bgcolor: '#cbd5e1',
										},
									}}
								>
									{isSubmitting ? 'Creating...' : 'Create Product'}
								</Button>
							)}
						</Box>
					</Card>
				</Box>
			</Box>
		</UserLayout>
	)
}

export default CreateProduct
